import CalendarProgress from "images/svg/icon-calendar-arrow.svg";
import { parseISOWithoutTime, getDateOptions } from "helpers/date-helpers";
import {
  startOfMonth,
  startOfWeek,
  isAfter,
  endOfMonth,
  addDays,
  isBefore,
  format,
  isSameDay,
  differenceInCalendarDays,
  subMonths,
  addMonths,
} from "date-fns";
import { isAdmin } from "components/source/hoc/with-user-data";
import { dateFnsFormats } from "rtr-constants";
import type { WorkingFilters } from "components/types/working-filters";
import type { DateOptions } from "components/types/date-options";
import type { ShippingCutoff } from "@rtr/godmother";
import { useAppSelector } from "../../../../../../util/store-types";

type RentalDateCalendarProps = {
  dateSelection?: string | null;
  duration: NonNullable<WorkingFilters["duration"]>;
  dateOptions: DateOptions;
  deliveryCutoffs: ShippingCutoff[];
  onClickDate: (date: string, lastRenderedCalendarDate: Date) => void;
  referenceDateValue: Date;
  setReferenceDateValue: (newReferenceDate: Date) => void;
};

export default function RentalDateCalendar({
  dateSelection,
  duration,
  dateOptions,
  deliveryCutoffs,
  onClickDate,
  referenceDateValue,
  setReferenceDateValue,
}: RentalDateCalendarProps) {
  const userData = useAppSelector(state => state.userData);

  //dateOptions on state only returns blackoutDays, this adds dateStart and dateEnd
  const fullDateOptions = getDateOptions(dateOptions);

  const getEarliestDeliveryDate = () => {
    const [firstDeliveryCutoff] = deliveryCutoffs || [];
    const { deliveryDate } = firstDeliveryCutoff || {};

    return deliveryDate ? parseISOWithoutTime(deliveryDate) : deliveryDate;
  };

  const getEarliestRentalStartDate = () => {
    const { dateStart } = fullDateOptions || "";

    return dateStart ? parseISOWithoutTime(dateStart) : dateStart;
  };

  const getFirstSelectableDate = () => {
    const dateStart = getEarliestRentalStartDate();
    const deliveryDate = getEarliestDeliveryDate();
    //this is a workaround for the CX team that need as
    //first selectable day the start date
    if (isAdmin(userData)) {
      return dateStart;
    }

    if (dateStart && deliveryDate) {
      return isAfter(dateStart, deliveryDate) ? dateStart : deliveryDate;
    } else {
      return dateStart ? dateStart : deliveryDate;
    }
  };

  const getLatestRentalStartDate = () => {
    const { dateEnd } = fullDateOptions || {};
    return dateEnd ? parseISOWithoutTime(dateEnd) : dateEnd;
  };

  const getFirstDayOfCalendar = () => {
    const monthStart = startOfMonth(referenceDateValue);
    //Monday is default week start
    const weekStart = startOfWeek(monthStart, { weekStartsOn: 1 });

    return weekStart;
  };

  const getDays = () => {
    const days = [];

    let day = getFirstDayOfCalendar();

    while (days.length < 42) {
      // Fill the calendar with days until we have 42 days (6 weeks)
      // This is to ensure the calendar always has 6 rows
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };

  const getLastCalendarDate = () => {
    return getDays().at(-1) as Date;
  };

  const canPageBack = () => {
    const earliestDate = getEarliestRentalStartDate();

    return !earliestDate || isBefore(earliestDate, getFirstDayOfCalendar());
  };

  const canPageForward = () => {
    const latestDate = getLatestRentalStartDate();
    const endOfCurrentMonth = endOfMonth(referenceDateValue);

    return !latestDate || isAfter(latestDate, endOfCurrentMonth);
  };

  const renderMonthHeader = () => {
    const pageBack = canPageBack();
    const pageForward = canPageForward();

    const containerClass = "flex justify-between w-full py-1";

    const previousMonthReferenceDate = subMonths(referenceDateValue, 1);
    const nextMonthReferenceDate = addMonths(referenceDateValue, 1);
    const monthYear = format(referenceDateValue, dateFnsFormats.MMMM_YYYY);

    const renderMonthBackButton = (invisible = false) => {
      if (invisible) {
        return <CalendarProgress className="invisible" />;
      }

      return (
        <CalendarProgress
          className="cursor-pointer"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => updateReferenceDate(previousMonthReferenceDate, e)}
          data-test-id="month-backward-button"
        />
      );
    };

    const renderMonthForwardButton = (invisible = false) => {
      if (invisible) {
        return <CalendarProgress className="scale-x-[-1] invisible" />;
      }
      return (
        <CalendarProgress
          className="scale-x-[-1] cursor-pointer"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => updateReferenceDate(nextMonthReferenceDate, e)}
          data-test-id="month-forward-button"
        />
      );
    };

    if (pageBack && !pageForward) {
      return (
        <div className={containerClass}>
          {renderMonthBackButton()}
          <p className="text-lg">{monthYear}</p>
          {renderMonthForwardButton(true)}
        </div>
      );
    } else if (pageForward && !pageBack) {
      return (
        <div className={containerClass}>
          {renderMonthBackButton(true)}
          <p className="text-lg">{monthYear}</p>
          {renderMonthForwardButton()}
        </div>
      );
    } else {
      return (
        <div className={containerClass}>
          {renderMonthBackButton()}
          <p className="text-lg">{monthYear}</p>
          {renderMonthForwardButton()}
        </div>
      );
    }
  };

  const updateReferenceDate = (date: Date, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    setReferenceDateValue(date);
  };

  const changeSelectedDate = (date: Date, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const asString = format(date, dateFnsFormats.YYYY_MM_DD);
    const lastRenderedCalendarDate = getLastCalendarDate();

    onClickDate(asString, lastRenderedCalendarDate);
  };

  const isBlackoutDay = (date: Date) => {
    const { blackoutDays = [] } = fullDateOptions || [];
    const asString = format(date, dateFnsFormats.YYYY_MM_DD);

    return blackoutDays.includes(date) || blackoutDays.includes(asString);
  };

  const selectedDateAsDateInstance = () => {
    if (!dateSelection) return;

    return parseISOWithoutTime(dateSelection);
  };

  const isSelectableDay = (date: Date) => {
    const firstSelectableDate = getFirstSelectableDate();

    const isOnOrAfterEarliestDate = isSameDay(date, firstSelectableDate) || isAfter(date, firstSelectableDate);
    const latestDate = getLatestRentalStartDate();
    //In some cases, we don't pass in an end date (i.e. all future date are valid)
    const isOnOrBeforeLatestDate = !latestDate || isSameDay(date, latestDate) || isBefore(date, latestDate);
    return !isBlackoutDay(date) && isOnOrAfterEarliestDate && isOnOrBeforeLatestDate;
  };

  const isWithinDurationWindow = (date: Date) => {
    const selectedDate = selectedDateAsDateInstance();

    if (!selectedDate) return;

    const diff = differenceInCalendarDays(date, selectedDate);

    return diff >= 0 && diff < parseInt(duration);
  };

  const isLastDateOfDurationWindow = (date: Date) => {
    const selectedDate = selectedDateAsDateInstance();

    if (!selectedDate) {
      return;
    }

    return isSameDay(date, addDays(selectedDate, parseInt(duration) - 1));
  };

  const renderWithinDurationWindow = (day: number, date: Date, isSelectable: boolean) => {
    const testIdDate = format(date, dateFnsFormats.YYYY_MM_DD);

    return (
      <p className="flex items-center justify-center h-10 w-10 md:h-11 md:w-14">
        <button
          className="flex items-center justify-center bg-wine100 w-full h-8 md:h-10 "
          onClick={e => changeSelectedDate(date, e)}
          data-test-id={`selected-within-duration-${testIdDate}`}
          disabled={!isSelectable}
          key={testIdDate}>
          {day}
        </button>
      </p>
    );
  };

  const renderSelectableDay = (day: number, date: Date) => {
    const testIdDate = format(date, dateFnsFormats.YYYY_MM_DD);
    return (
      <p className="flex items-center justify-center h-10 w-10 md:px-1 md:h-11 md:w-14">
        <button
          className="flex items-center justify-center text-darkGray hover:bg-wine100 hover:rounded-full md:hover:h-10 md:hover:w-10"
          onClick={e => changeSelectedDate(date, e)}
          data-test-id={`selectable-date-${testIdDate}`}
          key={testIdDate}>
          {day}
        </button>
      </p>
    );
  };

  const renderSelectedDay = (day: number, date: Date, first: boolean, isSelectable: boolean) => {
    const testIdDate = format(date, dateFnsFormats.YYYY_MM_DD);
    const backgroundAlignment = first ? "ml-auto" : "mr-auto";

    return (
      <p className="flex items-center justify-center h-10 w-10 md:h-11 md:w-14">
        <div className={`relative w-1/2 bg-wine100 z-10 ${backgroundAlignment} h-8 md:h-10`}></div>
        <button
          className="absolute flex items-center justify-center text-white bg-wine rounded-full z-20 h-8 w-8 md:h-10 md:w-10"
          onClick={e => changeSelectedDate(date, e)}
          data-test-id={`selected-date-${testIdDate}`}
          disabled={!isSelectable}
          key={testIdDate}>
          {day}
        </button>
      </p>
    );
  };

  const renderBlackoutDay = (day: number, date: Date) => {
    const testIdDate = format(date, dateFnsFormats.YYYY_MM_DD);
    return (
      <p
        className="flex items-center justify-center text-lightGray h-10 w-10 md:h-11 md:w-14"
        data-test-id={`blackout-date-${testIdDate}`}
        key={testIdDate}>
        {day}
      </p>
    );
  };

  return (
    <div
      className="flex flex-col items-center justify-center bg-white rounded-xl border border-lighterGray px-4 pt-3 pb-2"
      data-test-id="rental-date-calendar">
      {renderMonthHeader()}
      <div className="grid grid-cols-7 grid-rows-6 grid-flow-row">
        {["Mon", "Tues", "Weds", "Thur", "Fri", "Sat", "Sun"].map(d => (
          <p
            key={d}
            className="flex text-darkGray items-center justify-center p-1 h-10 w-10 md:h-11 md:w-14"
            data-test-id="weekday-header">
            {d}
          </p>
        ))}
        {getDays().map(date => {
          const dayOfDate = date.getDate();
          const selectedDate = selectedDateAsDateInstance();
          const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
          const lastDateOfDurationWindow = isLastDateOfDurationWindow(date);
          const blackoutDay = isBlackoutDay(date);
          const selectableDay = isSelectableDay(date);
          const withinDurationWindow = isWithinDurationWindow(date);

          if (withinDurationWindow && !isSelected && !lastDateOfDurationWindow) {
            return renderWithinDurationWindow(dayOfDate, date, selectableDay);
          } else if (isSelected) {
            return renderSelectedDay(dayOfDate, date, true, selectableDay);
          } else if (lastDateOfDurationWindow) {
            return renderSelectedDay(dayOfDate, date, false, selectableDay);
          } else if (blackoutDay && !selectableDay) {
            return renderBlackoutDay(dayOfDate, date);
          } else if (!blackoutDay && selectableDay) {
            return renderSelectableDay(dayOfDate, date);
          } else {
            return renderBlackoutDay(dayOfDate, date);
          }
        })}
      </div>
    </div>
  );
}
